import React, { Fragment } from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";

import colors from "core/colors";

const createHelpers = (width, height, css) => {
    // somehow sizes is ending up in markup, even if it is not a valid svg attribute
    // until we have a better solution, just render it empty, instead to '[Object object]'
    const sanitizeSizes = sizes =>
        Object.defineProperty(sizes, "toString", {
            value: () => "",
            enumerable: false
        });

    const getDimensions = (size, sizes) => {
        if (
            size &&
            typeof size.width === "number" &&
            typeof size.height === "number"
        ) {
            return size;
        }

        return size && sizes[size] ? sizes[size] : { width, height };
    };

    const getCss = (size, sizes, fillColor, fillColorRule, noStyles) => {
        if (noStyles) {
            return "";
        }

        const dimensions = getDimensions(size, sizes);
        const fillRule =
            fillColor && fillColorRule
                ? `${fillColorRule}{ fill: ${fillColor}; }`
                : "";
        return css`
            width: ${dimensions.width}px;
            height: ${dimensions.height}px;
            ${fillRule}
        `;
    };

    const propsToCss = ({ size, sizes, fillColor, fillColorRule, noStyles }) =>
        getCss(size, sizes, fillColor, fillColorRule, noStyles);

    return {
        getCss,
        getDimensions,
        propsToCss,
        sanitizeSizes
    };
};

const width = "24";
const height = "24";
const viewBox = "11.12 11.12 21.71 21.71";

const { getDimensions, getCss, propsToCss, sanitizeSizes } = createHelpers(
    width,
    height,
    css
);

const sizes = sanitizeSizes({
    xs: { width: 14, height: 14 },
    sm: { width: 16, height: 16 },
    md: { width: 20, height: 20 },
    lg: { width: 24, height: 24 },
    xl: { width: 40, height: 40 }
});

const Image = styled.svg`
    ${propsToCss}
`;

const children = (
    <Fragment>
        <path
            id="eyedropper-hollow"
            d="M15.0319 27.3134L14.7201 27.8651C14.218 28.7533 15.2123 29.7348 16.0939 29.2213L16.6285 28.9099L26.791 18.7474L25.2043 17.141C25.1836 17.141 17.4551 24.8892 15.0319 27.3134ZM28.5611 11.8469C29.0297 11.3788 29.6652 11.1158 30.3278 11.1158C30.9904 11.1158 31.6259 11.3788 32.0944 11.8469C32.5629 12.3151 32.8262 12.9501 32.8262 13.6121C32.8262 14.2742 32.5629 14.9092 32.0944 15.3773L30.3278 17.1425L30.8037 17.6181C31.5157 18.3294 31.5137 19.484 30.7993 20.1929C30.0892 20.8976 28.943 20.8956 28.2353 20.1885L27.7485 19.7021L17.7376 29.7343C16.9809 30.5139 13.7126 32.1791 11.8723 32.8263L11.1156 32.0702C11.7634 30.2315 13.3445 27.0982 14.2102 26.2097L24.2211 16.1776L23.7541 15.7109C23.0383 14.9957 23.0433 13.834 23.7651 13.125C24.4775 12.4254 25.6205 12.4303 26.3268 13.136L26.8181 13.6268L28.5611 11.8469Z"
            fill={colors.secondary.orca}
        />
    </Fragment>
);

const defaultProps = {
    children,
    viewBox,
    fillColor: null,
    fillColorRule: "&&& path, &&& use, &&& g, &&& circle",
    sizes,
    size: null
};

Image.propTypes /* remove-proptypes */ = {
    fillColor: PropTypes.string,
    fillColorRule: PropTypes.string,
    viewBox: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
    size: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
            height: PropTypes.number.isRequired,
            width: PropTypes.number.isRequired
        })
    ]),
    sizes: PropTypes.shape({
        height: PropTypes.number,
        width: PropTypes.number
    })
};

export default Object.assign(Image, {
    getDimensions,
    getCss,
    defaultProps,
    displayName: "Eyedropper"
});
